import React, { useState } from "react";
import {
  Form,
  DrawerProps,
  Drawer,
  Row,
  Button,
  Col,
  UploadFile,
  Card,
} from "antd";
import SVGIcon from "../../../../../utils/SVGIcon";
import Config from "../../../../../config/Config";
import Dragger from "antd/es/upload/Dragger";
import { UploadProps } from "antd/lib";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../utils/hooks/hooks";
import { importFile, setFileRowData, uplodeFile } from "../../utils/slice";
import {
  Notification,
  dataToFormDataConverter,
} from "../../../../../config/Global";
import { assignErrorToInput } from "../../../../../store/api";
import { RootState } from "../../../../../store/app";
import  DownloadIcons from "../../../../../assets/images/icon/downloadIcon.svg?react";
import  UploadIcon from "../../../../../assets/images/icon/uploadIcon.svg?react";
import  Dragicon from "../../../../../assets/images/icon/dragIcon.svg?react";
import {
  formatEditor,
  generateImportColumn,
  importErrorFunction,
  importTextFormatEditor,
} from "../../../../../utils/commonFunctions";
import AgGridWrapper from "../../../../../components/AgGridWrapper/AgGridWrapper";
import columnDefs from "../Listing/importColumnDefs";
interface AddDrawerProps extends DrawerProps {
  titleName: string;
  close: () => void;
}

/**
 * Renders an import component for adding a new career. 
 * The component allows the user to upload a file, verify its contents, 
 * and import the data into the system.
 *
 * @param {AddDrawerProps} props - The props for the component.
 * @param {string} props.titleName - The name of the career to be imported.
 * @param {function} props.close - The function to close the component.
 * @param {...any} props.rest - Additional props for the component.
 * @return {ReactElement} The rendered import component.
 */
const ImportComponent: React.FC<AddDrawerProps> = ({
  titleName,
  close,
  ...rest
}) => {
  const dispatch = useAppDispatch();
  const setStatusData = useState<any>();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [verify, setVerify] = useState<boolean>(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showAgGrid, setShowAgGrid] = useState<boolean>(false);

  const rowData: any = useAppSelector(
    (state: RootState) => state.career.fileRowdata
  );

  const agGridDisplay = showAgGrid ? { display: "block" } : { display: "none" };


    /**
   * Closes the drawer and resets the form fields, file list, and file row data.
   *
   * @return {void} 
   */
  const drawerClose = () => {
    setShowAgGrid(false);
    form.resetFields();
    setFileList([]);
    dispatch(setFileRowData(null));
    close();
  };

  /**
   * Handles the upload process asynchronously.
   *
   * upload the file to the server and set the file list to an empty array.
   * returns set row data for the grid from the server if the upload is successful
   * @return {Promise<void>} A promise that resolves when the upload process is complete.
   */
  const handleUpload = async () => {
    setVerify(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      dispatch(uplodeFile(dataToFormDataConverter({ doc_file: fileList[0] })))
        .then(() => {
          setShowAgGrid(true);
        })
        .catch((error: any) => {
          setShowAgGrid(false);
          if (error?.STATUS?.doc_file) {
            error?.STATUS?.doc_file.map((err: string) =>
              Notification.error({
                message: err,
              })
            );
          } else {
            assignErrorToInput(form, error?.STATUS);
          }
        });
      setVerify(false);
      setFileList([]);
    } catch (error: any) {
      setShowAgGrid(false);
      setVerify(false);
      if (error?.STATUS?.doc_file) {
        error?.STATUS?.doc_file.map((err: string) =>
          Notification.error({
            message: err,
          })
        );
      } else {
        assignErrorToInput(form, error?.STATUS);
      }
    }
  };

  const props: UploadProps = {

     // Removes a file from the file list.
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },

    // Sets the file list to contain only the given file and returns false.
    beforeUpload: (file) => {
      setFileList([file]);
      return false;
    },
    fileList,
  };

  /**
   * Upload the row data to the server if no errors are found.
   */
  const handleImport = async () => {
    setUploading(true);
    try {
      dispatch(importFile({ data: rowData?.data })).catch((error: any) => {
        assignErrorToInput(form, error?.STATUS);
      });
      setUploading(false);
      drawerClose();
    } catch (error: any) {
      setUploading(false);
      assignErrorToInput(form, error?.NOTIFICATION);
    }
  };

  /**
   * Downloads a sample file of careers from the server.
   */
  const handleDownload = async () => {
    setDownloading(true);
    const filePath = `/sampleFile/list-of-careers.xlsx`;
    const response = await fetch(filePath);
    const downloadLink = document.createElement("a");
    downloadLink.href = response.url;
    downloadLink.download = `sample_careers.xlsx`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    setDownloading(false);
  };

  return (
    <Drawer
      title={`Import ${titleName}`}
      placement="right"
      className="importDrawer"
      width={"90%"}
      onClose={drawerClose}
      destroyOnClose
      closeIcon={<SVGIcon icon="close" color={Config.themePrimaryBtn} />}
      {...rest}
    >
      <Row gutter={[16, 16]} className="importInfoText">
        <Col xs={{ span: 24 }} md={{ span: 18 }}>
          <p>
            Please download a sample file formate,this will help you to create a
            new draft considering what information is required.{" "}
          </p>
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 6 }}>
          <Button
            type="primary"
            onClick={handleDownload}
            loading={downloading}
            style={{ marginTop: 16 }}
          >
            <DownloadIcons />
            {downloading ? "Downloading" : "Download Sample File"}
          </Button>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={{ span: 24 }} md={{ span: 12 }}>
          <Button
            type="primary"
            onClick={handleUpload}
            disabled={fileList.length === 0}
            loading={verify}
            style={{ marginTop: 16 }}
          >
            <UploadIcon />
            {verify ? "Uploading" : "Upload & Verify"}
          </Button>
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 12 }}>
          <Dragger style={{ width: "100%", height: "300px" }} {...props}>
            <p className="ant-upload-drag-icon">
              <Dragicon />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
          </Dragger>
        </Col>
      </Row>
      <div style={agGridDisplay}>
        <div className="ag-theme-quartz">
          <AgGridWrapper
            rowData={rowData?.data || []}
            columnDefs={columnDefs}
            paginationPageSize={10}
            alwaysShowHorizontalScroll={true}
            alwaysShowVerticalScroll={true}
            pagination={true}
            // context={{ setStatusData }}
          />
        </div>
        <div style={{ marginTop: 20 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Card
                title="Error"
                bordered={false}
                style={{ background: "#d0312d", color: "white" }}
              >
                {rowData?.error_count ? rowData?.error_count : 0}
              </Card>
            </Col>
            <Col span={8}>
              <Card
                title="Verified Ok"
                bordered={false}
                style={{ background: "#3bb143", color: "white" }}
              >
                {rowData?.verified ? rowData?.verified : 0}
              </Card>
            </Col>
            <Col span={8}>
              <Card
                title="Total"
                bordered={false}
                style={{ background: "#29c5f6", color: "white" }}
              >
                {rowData?.total ? rowData?.total : 0}
              </Card>
            </Col>
          </Row>
        </div>
      </div>
      <div className="importDrawerBtn">
        <Button
          key="change-order-2"
          htmlType="button"
          className="cancelBtn"
          onClick={drawerClose}
          style={{ marginTop: 16 }}
        >
          Cancel
        </Button>
        {rowData?.error_count === 0 && showAgGrid ? (
          <Button
            type="primary"
            onClick={handleImport}
            loading={uploading}
            style={{ marginTop: 16 }}
          >
            {uploading ? "Uploading" : "Upload "}
          </Button>
        ) : (
          ""
        )}
      </div>
    </Drawer>
  );
};
export default ImportComponent;
UploadIcon;
